import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject, forkJoin } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { HttpService } from './http.service';
import { HttpClient } from '@angular/common/http';

export interface GlassBoxConfig {
  MaskFields: string;
  Version: string;
}

export interface AppConfig {
  environment: string;
  production: boolean;
  recaptchaSiteKey: string;
  isInnerNetwork: boolean;
  disableRedirectStep: boolean;
  displaySkipSteps: boolean;
  gtmKey: string;
  gtmKeyWebView: string;
  timeoutMinutes: number;
  warningMinutes: number;
  GlassBox: GlassBoxConfig;
}

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private httpService = inject(HttpService);
  private httpClient = inject(HttpClient);
  private config: AppConfig | null = null;
  private configLoaded$ = new BehaviorSubject<boolean>(false);

  constructor() {
  }

  public loadConfig(): Observable<any> {
    const configPath = `configs/config.json`;
    const networkPath = `network/isInnerNetwork`;

    return forkJoin({
      config: this.httpClient.get<AppConfig>(configPath),
      network: this.httpService.get<boolean>(networkPath)
    }).pipe(
      tap(res => {
        if(res.config){
          this.config = res.config;
        }
        if(res.network.returnCode === 0 && this.config) {
          this.config.isInnerNetwork = res.network.result;
        }
        this.configLoaded$.next(true);
      }),
      catchError(error => {
        console.error('Failed to load configuration:', error);
        this.configLoaded$.next(false);
        throw error;
      })
    )
  }

  public getConfig(): AppConfig | null {
    return this.config;
  }

  public isConfigLoaded(): Observable<boolean> {
    return this.configLoaded$.asObservable();
  }

  public getValue<T>(key: string): T | undefined {
    if (!this.config) {
      return undefined;
    }

    return this.getNestedValue(this.config, key);
  }

  public getValueOrDefault<T>(key: string, defaultValue: T): T {
    const value = this.getValue<T>(key);
    return value !== null && value !== undefined ? value : defaultValue;
  }

  private getNestedValue<T>(obj: any, key: string): T | undefined {
    const keys = key.split('.');
    let current = obj;

    for (const k of keys) {
      if (current && typeof current === 'object' && k in current) {
        current = current[k];
      } else {
        return undefined;
      }
    }

    return current;
  }
}
